import "@mdxeditor/editor/style.css";
import LoadingButton from "@mui/lab/LoadingButton";
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { enUS, pl } from "date-fns/locale";
import React, { useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
    EmailBodyEditor,
    EmailRecipients,
    EmailRecipientsSelect,
    EmailSubjectInput,
} from "../../../components/dialog/EmailSender";
import { ProductDocumentsSelector } from "../../../components/documents/ProductDocumentsSelector";
import { useCustomersByIds } from "../../../services/customersService";
import {
    useEmailDrafts,
    useGenerateMailDraft,
    useOfferDrafts,
} from "../../../services/draftsService";
import { useCurrentSeller } from "../../../services/usersApi";
import { useLanguage } from "../../../utils";
import { OfferDraftPreview } from "../../SalesDraftsPage/DraftPreview/OfferDraftPreview";
import { EmailFooterPreview } from "../../SalesSellersPage/EmailFooter/EmailFooterPreview";
import { EmailFooterSelector } from "../../SalesSellersPage/EmailFooter/EmailFooterSelector";
import { useIsOfferFormDataValid } from "./useIsOfferFormDataValid";
import { AddLocalFilesForm } from "./AddLocalFilesForm";
import { useCustomersApprovedClauses } from "../../../services/customersService";
import { isEmptyArray } from "../../../helpers/methods";

export const SendOfferTab = (props) => {
    const { offer, onSaveAndSend, isRequestSend, getRequestDataForGenerateOffer, bodyEditorRef:ref } = props;
    const { t } = useTranslation();

    // const ref = useRef(null);

    const { setValue } = useFormContext();
    const customerIds = useWatch({ name: "customerIds" }) || [];
    const expirationDate = useWatch({ name: "expireDate" });
    const productDiscounts = useWatch({
        name: "discount",
    });
    const productIds = Object.keys(productDiscounts);
    const draftId = useWatch({ name: "draftId" });
    const emailSubject = useWatch({ name: "email_subject" })
    const initialEmailBody = useWatch({ name: "email_body" });

    const setExpirationDate = (date) => {
        setValue("expireDate", date || undefined, { shouldDirty: true, shouldTouch: true });
    };

    const { data: customers, isLoading: isLoadingCustomerData } = useCustomersByIds(customerIds);
    const customerPrimaryEmails =
        customers?.map((c) => c.person?.email?.email_address || c.company?.email?.email_address).filter(Boolean) || [];
    const customerEmails =
        customers?.reduce((emails, customer) => ([ ...emails, ...customer.email_addresses]), [])

    const { data: emailDraftsResponse } = useEmailDrafts();
    const emailDrafts = (emailDraftsResponse?.results || []).filter((draft) => draft.active);
    const { data: offerDraftsResponse } = useOfferDrafts();
    const offerDrafts = (offerDraftsResponse?.results || []).filter((draft) => draft.active);
    const { data: seller } = useCurrentSeller();

    const [selectedCustomerEmails, setSelectedCustomerEmails] = useState([])
    const [selectedEmailDraft, setSelectedEmailDraft] = useState(null);
    const [selectedOfferDraft, setSelectedOfferDraft] = useState(null);
    const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
    const [selectedEmailFooter, setSelectedEmailFooter] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]) 


    const { data: generatedDraftEmail } = useGenerateMailDraft(selectedEmailDraft?.id);

    useEffect(() => {
        if (draftId && offerDrafts.length > 0) {
            const draft = offerDrafts.find((d) => d.id === draftId) || null;
            setSelectedOfferDraft(draft);
        }
    }, [draftId, offerDrafts]);

    const handleOfferDraftChange = (draft) => {
        setSelectedOfferDraft(draft);
        setValue("draftId", draft?.id || undefined, { shouldDirty: true });
    };

    // Update email body and subject with the selected draft
    useEffect(() => {
        if (!generatedDraftEmail) {
            return;
        }
        setValue("email_subject", generatedDraftEmail.subject)
        ref.current?.setMarkdown(generatedDraftEmail.body);
    }, [generatedDraftEmail]);

    useEffect(() => {
        if (!isLoadingCustomerData) {
            setSelectedCustomerEmails(customerPrimaryEmails)
        }
    }, [isLoadingCustomerData]);

    const handleChangeCustomerEmails = (e)=> {
        setSelectedCustomerEmails(e.target.value)
    }


    const handleChangeEmailSubject = (emailSubject)=>{
        setValue("email_subject", emailSubject)
    }

    const handleSaveAndSend = () => {
        const body = ref.current?.getHtml() || "";

        onSaveAndSend({
            email_subject: emailSubject || "",
            email_body: body,
            document_ids: selectedDocumentIds,
            selected_files: selectedFiles.map(fileObj=>fileObj.file),
            email_footer_id: selectedEmailFooter?.id || null,
            email_addresses: selectedCustomerEmails,
        });
    };

    const { isValid: canSave } = useIsOfferFormDataValid({
        customerIds: "customerIds",
        discount: "discount",
    });

    const selectedCustomerIds = useWatch({ name: "customerIds" });
    const customerSearchParams = new URLSearchParams()
    if (selectedCustomerIds && selectedCustomerIds.length > 0) {
        for (let customerId of selectedCustomerIds) {
            customerSearchParams.append("ids", customerId);
        }
    }
    const { isLoading: isLoadingCustomers, data: selectedCustomers } = useCustomersApprovedClauses(customerSearchParams);
    const allCustomersHasApprovedClause = selectedCustomers?.length > 0 && selectedCustomers.every((customer) => customer.has_approved_clause);


    const canSaveAndSend =
        canSave &&
        // selectedEmailDraft != null &&
        selectedOfferDraft != null &&
        emailSubject && 
        emailSubject.trim() !== "" &&
        selectedEmailFooter != null && 
        allCustomersHasApprovedClause &&
        selectedCustomerEmails &&
        !isEmptyArray(selectedCustomerEmails)
        // && expirationDate != null;

    return (
        <Grid container spacing={2} sx={{ padding: 2 }}>
            <Grid item xs={8}>
                <EmailDraftSelect
                    drafts={emailDrafts}
                    selectedDraft={selectedEmailDraft}
                    onDraftChange={setSelectedEmailDraft}
                />
            </Grid>
            <Grid item xs={4}>
                <ExpirationDateSelect value={expirationDate || null} onChange={setExpirationDate} />
            </Grid>
            <Grid item xs={8}>
                <EmailRecipientsSelect emailOptions={customerEmails} seletedEmails={selectedCustomerEmails} setSelectedEmails={handleChangeCustomerEmails}/>
            </Grid>
            <Grid item xs={4}>
                <OfferDraftPreview
                    drafts={offerDrafts}
                    selectedDraft={selectedOfferDraft}
                    onDraftChange={handleOfferDraftChange}
                    offerId={offer.id}
                    selectedDocumentIds={selectedDocumentIds}
                    getRequestDataForGenerateOffer={getRequestDataForGenerateOffer}
                />
            </Grid>
            <Grid item xs={8}>
                <EmailSubjectInput value={emailSubject} onChange={handleChangeEmailSubject} />
            </Grid>
            <Grid item xs={4}>
                <EmailFooterSelector
                    footers={seller?.email_footers || []}
                    selectedFooter={selectedEmailFooter}
                    onFooterChange={setSelectedEmailFooter}
                />
            </Grid>
            <Grid item xs={12}  textAlign={"left"}>
                <EmailBodyEditor ref={ref} initialText={initialEmailBody}/>
            </Grid>
            {selectedEmailFooter && (
                <Grid item xs={12}>
                    <EmailFooterPreview htmlContent={selectedEmailFooter.html_content} />
                </Grid>
            )}
            <Grid item xs={12}>
                <ProductDocumentsSelector
                    selectedDocumentIds={selectedDocumentIds}
                    onChange={setSelectedDocumentIds}
                    productIds={productIds}
                />
            </Grid>
            <Grid item xs={6}>
                <AddLocalFilesForm selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles}/>
            </Grid>
            <Grid item xs={12}>
                <Grid container justifyContent={"center"} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                        <LoadingButton
                            variant="contained"
                            fullWidth
                            onClick={handleSaveAndSend}
                            disabled={!canSaveAndSend || !allCustomersHasApprovedClause || isRequestSend}
                            loading={isRequestSend}
                        >
                            {t("offers.SendOfferTab.saveAndSend")}
                        </LoadingButton>
                        {!allCustomersHasApprovedClause && <Typography
                            variant={"h7"}
                            color={"error"}
                        >
                            {`*${t("offers.OfferCreateDialog.atLeastOnCustomerDontHaveApprovedClauses")}`}
                            </Typography>}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

const ExpirationDateSelect = (props) => {
    const { value, onChange } = props;
    const { t } = useTranslation();
    const language = useLanguage();

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    return (
        <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={language === "pl" ? pl : enUS}
        >
            <DatePicker
                label={`${t("offers.SendOfferTab.expirationDate")}`}
                value={value}
                onChange={(newValue) => onChange(newValue)}
                minDate={tomorrow}
                renderInput={(params) => <TextField fullWidth {...params} />}
            />
        </LocalizationProvider>
    );
};

const EmailDraftSelect = (props) => {
    const { drafts, selectedDraft, onDraftChange } = props;
    const { t } = useTranslation();

    const inputLabelId = "email-draft-select-label";

    return (
        <FormControl fullWidth>
            <InputLabel id={inputLabelId}>{t("offers.SendOfferTab.selectEmailDraft")}</InputLabel>
            <Select
                labelId={inputLabelId}
                value={selectedDraft?.id || ""}
                label={t("offers.SendOfferTab.selectEmailDraft")}
                onChange={(event) => {
                    const draftId = event.target.value;
                    const draft = drafts.find((d) => d.id === draftId) || null;
                    onDraftChange(draft);
                }}
            >
                {drafts.map((draft) => (
                    <MenuItem key={draft.id} value={draft.id}>
                        {draft.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};
